import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api, { setAuthToken } from '../services/api';

const AUTH_STORAGE_KEY = 'constadmin.auth';
const ACTIVE_ORGANISATION_KEY = 'constadmin.activeOrganisationId';

const AuthContext = createContext(null);

function readStoredAuth() {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function readActiveOrganisationId() {
  const id = Number(localStorage.getItem(ACTIVE_ORGANISATION_KEY));
  return Number.isInteger(id) && id > 0 ? id : null;
}

function rememberActiveOrganisation(user) {
  if (user?.organisationId) {
    localStorage.setItem(ACTIVE_ORGANISATION_KEY, String(user.organisationId));
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => readStoredAuth()?.token || null);
  const [user, setUser] = useState(() => readStoredAuth()?.user || null);
  const [loading, setLoading] = useState(true);

  const persistAuth = useCallback((nextToken, nextUser) => {
    setToken(nextToken);
    setUser(nextUser);
    setAuthToken(nextToken);

    if (nextToken && nextUser) {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ token: nextToken, user: nextUser }));
      rememberActiveOrganisation(nextUser);
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  }, []);

  const logout = useCallback(() => {
    persistAuth(null, null);
  }, [persistAuth]);

  const login = useCallback(async (email, password) => {
    const organisationId = readActiveOrganisationId();
    const response = await api.post('/api/auth/login', {
      email,
      password,
      ...(organisationId ? { organisationId } : {}),
    });
    persistAuth(response.data.token, response.data.user);
    return response.data.user;
  }, [persistAuth]);

  const switchOrganisation = useCallback(async (organisationId) => {
    const response = await api.post('/api/auth/switch-organisation', { organisationId });
    persistAuth(response.data.token, response.data.user);
    window.location.assign('/');
    return response.data.user;
  }, [persistAuth]);

  const refreshUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return null;
    }

    try {
      const response = await api.get('/api/auth/me');
      persistAuth(token, response.data.user);
      return response.data.user;
    } catch (error) {
      logout();
      return null;
    } finally {
      setLoading(false);
    }
  }, [token, persistAuth, logout]);

  useEffect(() => {
    setAuthToken(token);
    if (!token) {
      setLoading(false);
      return;
    }

    refreshUser();
  }, [token, refreshUser]);

  const value = useMemo(() => ({
    token,
    user,
    loading,
    isAuthenticated: Boolean(token && user),
    login,
    logout,
    refreshUser,
    switchOrganisation,
  }), [token, user, loading, login, logout, refreshUser, switchOrganisation]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}

export default AuthContext;
